import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Loader2, AlertTriangle, Stethoscope, Clock, Phone, ChevronRight, RefreshCw, User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDateIST(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
}
function formatTimeIST(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
}

export default function PendingReasonsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/consult-cards/pending-reasons`, { withCredentials: true });
      setData(res);
    } catch { toast.error('Failed to load pending reasons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>;

  const isAdmin = user?.role === 'admin';
  const total = data?.total || 0;

  return (
    <div className="space-y-4" data-testid="pending-reasons-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg sm:text-xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Pending Prescription Reasons
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isAdmin ? 'Consult cards where doctors modified prescriptions without providing a reason' : 'Your consult cards awaiting reason for prescription changes'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-xs ${total > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
            {total} pending
          </Badge>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchPending(); }} className="rounded-full gap-1 text-xs h-8">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {total === 0 ? (
        <Card className="border-dashed border-emerald-200">
          <CardContent className="py-10 text-center space-y-2">
            <AlertTriangle className="w-8 h-8 text-emerald-300 mx-auto" />
            <p className="text-sm text-muted-foreground">No pending reasons</p>
            <p className="text-xs text-muted-foreground">All prescription changes have been documented</p>
          </CardContent>
        </Card>
      ) : isAdmin && data?.by_doctor ? (
        // Admin view: grouped by doctor
        <div className="space-y-4">
          {data.by_doctor.map(group => (
            <div key={group.doctor_name}>
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="w-4 h-4 text-violet-500" />
                <h2 className="text-sm font-semibold text-slate-700">{group.doctor_name}</h2>
                <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">{group.cards.length} pending</Badge>
              </div>
              <div className="space-y-1.5 ml-6">
                {group.cards.map(card => (
                  <PendingCard key={card.id} card={card} onClick={() => navigate(`/consult-cards/${card.id}`)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Doctor view: flat list
        <div className="space-y-1.5">
          {(data?.cards || []).map(card => (
            <PendingCard key={card.id} card={card} onClick={() => navigate(`/consult-cards/${card.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingCard({ card, onClick }) {
  return (
    <Card className="border-amber-100 hover:border-amber-300 cursor-pointer transition-colors active:scale-[0.99]" onClick={onClick} data-testid={`pending-card-${card.id}`}>
      <CardContent className="py-2.5 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{card.patient_name}</p>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 flex-wrap">
                <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{card.patient_phone}</span>
                <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{formatDateIST(card.created_at)} {formatTimeIST(card.created_at)}</span>
                {card.eden_service_name && <span className="text-emerald-500">{card.eden_service_name}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 animate-pulse">Needs Reason</Badge>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
